import { BoxV2 as Box, Flex } from 'portal-commons';
import {
  FunctionComponent,
  MouseEvent,
  ComponentProps,
  useEffect,
} from 'react';

interface ModalV2Props {
  open?: boolean;
  onClose?: () => void;
  width?: string | number;
  padding?: string | number;
  sx?: ComponentProps<typeof Box>['sx'];
  backdropSx?: ComponentProps<typeof Box>['sx'];
  'data-testid'?: string;
  children: React.ReactNode;
  ariaLabelledby?: string;
  ariaDescribedby?: string;
  enableBackdropClick?: boolean;
}

const ModalV2: FunctionComponent<ModalV2Props> = ({
  open = false,
  onClose,
  width = '525px',
  padding = '28px 30px 9px 30px',
  sx,
  backdropSx,
  'data-testid': testId,
  children,
  ariaLabelledby,
  ariaDescribedby,
  enableBackdropClick = false,
}) => {
  useEffect(() => {
    if (!open) {
      return;
    }
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, [open]);

  const handleBackdropClick = (e: MouseEvent) => {
    if (e.target === e.currentTarget && enableBackdropClick) {
      onClose?.();
    }
  };

  return (
    <Flex
      onClick={handleBackdropClick}
      aria-hidden={!open}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1300, // Corresponds to MUI Dialog z-index
        backdropFilter: 'blur(1px)',
        visibility: open ? 'visible' : 'hidden',
        opacity: open ? 1 : 0,
        transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        ...backdropSx,
      }}
    >
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
        sx={{
          width,
          padding,
          borderRadius: '4px',
          backgroundColor: '#FFFFFF',
          boxSizing: 'border-box',
          ...sx,
        }}
        data-testid={testId}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
            rowGap: '8px',
          }}
        >
          {children}
        </Flex>
      </Box>
    </Flex>
  );
};

export default ModalV2;
